import {
  ButtonERP,
  DataTablePrimeReact,
  TableERP,
} from "@finanedi.es/storybook"
import { getUnsignedAlbaran } from "api/Albaran.service"
import { DocumentoComercialValidacionDtoProps } from "api/DTOs/DocumentoComercialValidacionDto"
import { useDocumentoComercialPendienteFirmar } from "api/DocumentoComercial.hook"
import { getUnsignedFacturas } from "api/Factura.service"
import { getUnsignedPedido } from "api/Pedido.service"
import { getUnsignedPresupuesto } from "api/Presupuesto.service"
import {
  TIPO_DOCUMENTO_ENUM,
  TipoDocumentoEnumType,
} from "api/Types/TipoDocumentoEnum"
import { DocumentoComercialValidacionDtoQueryParamsProps } from "api/interfaces/DocumentoComercialValidacionDtoQueryParamsProps"
import { TabValidaciones } from "features/Validaciones/components/TabValidaciones/TabValidaciones"
import { useFilterMenu } from "hooks/useFilterMenu"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import FilterMenu from "./FilterMenu"
import { ModalAutorizarDocumentos } from "./components/ModalAceptarDocumentos/ModalAutorizarDocumentos"
import { ESTADO_APROBACION_DOCUMENTOS } from "./constant"
import { APROBAR_DOCUMENTOS_COLUMNS } from "./constants/AprobarDocumentosColumn"
import "./style.css"

const Documentos = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()
  const [tabSelected, setTabSelected] = useState<TipoDocumentoEnumType>(
    state?.selectedTable ?? TIPO_DOCUMENTO_ENUM.FACTURA
  )

  const dtRef =
    useRef<DataTablePrimeReact<DocumentoComercialValidacionDtoProps[]>>(null)

  const fetchItems = useCallback(
    () => (queryState: DocumentoComercialValidacionDtoQueryParamsProps) => {
      switch (tabSelected) {
        case TIPO_DOCUMENTO_ENUM.FACTURA:
          return getUnsignedFacturas(queryState)
        case TIPO_DOCUMENTO_ENUM.ALBARAN:
          return getUnsignedAlbaran(queryState)
        case TIPO_DOCUMENTO_ENUM.PEDIDO:
          return getUnsignedPedido(queryState)
        case TIPO_DOCUMENTO_ENUM.PRESUPUESTO:
          return getUnsignedPresupuesto(queryState)
        default:
          return getUnsignedFacturas(queryState)
      }
    },
    [tabSelected]
  )

  const [selection, setSelection] = useState<
    DocumentoComercialValidacionDtoProps[]
  >([])

  const { documentoComercialData } = useDocumentoComercialPendienteFirmar()

  const { items, isLoading, setServiceQuery } = useFilterMenu({
    serviceCall: fetchItems,
  })

  const handleRedirectToDetail = (
    rowData: DocumentoComercialValidacionDtoProps[]
  ): void => {
    history.push({
      pathname: ROUTES.VALIDACIONES_DOCUMENTOS_DETAIL(rowData[0].id),
      state: {
        selectedTable: tabSelected,
        validacionesSelected: rowData,
        docType: tabSelected,
      },
    })
  }

  return (
    <div className='tw-overflow-hidden tw-bg-white !tw-h-[calc(100vh-64px)] tw-text-base tw-font-medium'>
      <TabValidaciones />
      <div className='tw-bg-white tw-h-full'>
        <div className='!tw-px-4 sm:!tw-px-8 tw-flex tw-flex-col xl:tw-flex-row tw-gap-2 tw-justify-between tw-items-center tw-py-2.5 tw-shadow-3xl tw-rounded-t-xl mb-1'>
          <p className='tw-font-normal tw-w-fit tw-text-xl tw-m-0 tw-flex tw-flex-row tw-items-center'>
            <span className='tw-w-fit'>
              {ESTADO_APROBACION_DOCUMENTOS(t)?.[tabSelected]?.pluralText}
              {t("documentos.validar.validateLabel")}
            </span>
            (
            {isLoading ? (
              <Skeleton className='!tw-h-5 !tw-w-8 !tw-mx-1' />
            ) : (
              <span>{items?.length ?? 0}</span>
            )}
            )
          </p>
          <div className='tw-flex tw-w-full tw-justify-center xl:tw-justify-end'>
            <div className='tw-w-full md:!tw-w-fit tw-flex tw-items-center tw-flex-col xl:tw-flex-row tw-justify-center xl:tw-justify-end tw-gap-1 lg:tw-gap-3'>
              <FilterMenu serviceQuery={setServiceQuery} />
              <span className='tw-w-full md:tw-w-fit tw-relative'>
                <div className='tw-grid tw-grid-cols-4 tw-gap-1 tw-h-10 tw-w-auto tw-p-1 tw-bg-neutral-10 tw-rounded tw-cursor-pointer !tw-text-sm'>
                  <span
                    key='facturas'
                    className={`tw-px-6 tw-flex tw-items-center tw-h-8 tw-font-medium tw-rounded tw-justify-center tw-text-nowrap ${
                      tabSelected === TIPO_DOCUMENTO_ENUM.FACTURA
                        ? "tw-bg-white tw-shadow"
                        : ""
                    } `}
                    onClick={() => {
                      setTabSelected(TIPO_DOCUMENTO_ENUM.FACTURA)
                    }}
                  >
                    <span className='tw-relative'>
                      {`${
                        ESTADO_APROBACION_DOCUMENTOS(t)[
                          TIPO_DOCUMENTO_ENUM.FACTURA
                        ]?.text
                      }`}
                      {!!documentoComercialData?.facturas && (
                        <span className='tw-absolute tw-top-0 tw--right-2.5 tw-flex tw-h-2 tw-w-2'>
                          <span className='tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-warning tw-opacity-75' />
                          <span className='tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-warning' />
                        </span>
                      )}
                    </span>
                  </span>

                  <span
                    key='pagares'
                    className={`tw-px-6 tw-flex tw-items-center tw-h-8 tw-font-medium tw-rounded tw-justify-center tw-text-nowrap ${
                      tabSelected === TIPO_DOCUMENTO_ENUM.ALBARAN
                        ? "tw-bg-white tw-shadow"
                        : ""
                    }`}
                    onClick={() => {
                      setTabSelected(TIPO_DOCUMENTO_ENUM.ALBARAN)
                    }}
                  >
                    <span className='tw-relative'>
                      {`${
                        ESTADO_APROBACION_DOCUMENTOS(t)[
                          TIPO_DOCUMENTO_ENUM.ALBARAN
                        ]?.text
                      }`}
                      {!!documentoComercialData?.albaranes && (
                        <span className='tw-absolute tw-top-0 tw--right-2.5 tw-flex tw-h-2 tw-w-2'>
                          <span className='tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-warning tw-opacity-75' />
                          <span className='tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-warning' />
                        </span>
                      )}
                    </span>
                  </span>
                  <span
                    key='albaranes'
                    className={`tw-px-6 tw-flex tw-items-center tw-h-8 tw-font-medium tw-rounded tw-justify-center tw-text-nowrap ${
                      tabSelected === TIPO_DOCUMENTO_ENUM.PEDIDO
                        ? "tw-bg-white tw-shadow"
                        : ""
                    }`}
                    onClick={() => {
                      setTabSelected(TIPO_DOCUMENTO_ENUM.PEDIDO)
                    }}
                  >
                    <span className='tw-relative'>
                      {`${
                        ESTADO_APROBACION_DOCUMENTOS(t)[
                          TIPO_DOCUMENTO_ENUM.PEDIDO
                        ]?.text
                      }`}
                      {!!documentoComercialData?.pedidos && (
                        <span className='tw-absolute tw-top-0 tw--right-2.5 tw-flex tw-h-2 tw-w-2'>
                          <span className='tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-warning tw-opacity-75' />
                          <span className='tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-warning' />
                        </span>
                      )}
                    </span>
                  </span>
                  <span
                    key='presupuestos'
                    className={`tw-px-6 tw-flex tw-items-center tw-h-8 tw-font-medium tw-rounded tw-justify-center tw-text-nowrap ${
                      tabSelected === TIPO_DOCUMENTO_ENUM.PRESUPUESTO
                        ? "tw-bg-white tw-shadow"
                        : ""
                    }`}
                    onClick={() => {
                      setTabSelected(TIPO_DOCUMENTO_ENUM.PRESUPUESTO)
                    }}
                  >
                    <span className='tw-relative'>
                      {`${
                        ESTADO_APROBACION_DOCUMENTOS(t)[
                          TIPO_DOCUMENTO_ENUM.PRESUPUESTO
                        ]?.text
                      }`}
                      {!!documentoComercialData?.presupuestos && (
                        <span className='tw-absolute tw-top-0 tw--right-2.5 tw-flex tw-h-2 tw-w-2'>
                          <span className='tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-warning tw-opacity-75' />
                          <span className='tw-relative tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full tw-bg-warning' />
                        </span>
                      )}
                    </span>
                  </span>
                </div>
              </span>
              <ButtonERP
                customize={{
                  size: "md",
                  type: "secondary",
                }}
                label={`${t("documentos.validar.validateButton")}`}
                onClick={() => handleRedirectToDetail(selection)}
                disabled={selection.length === 0}
              />
            </div>
          </div>
        </div>

        <div className='tw-flex tw-flex-col tw-h-[calc(100%-88px)] tw-relative'>
          <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
            <TableERP
              refTable={dtRef}
              columns={APROBAR_DOCUMENTOS_COLUMNS(
                t,
                handleRedirectToDetail,
                tabSelected
              )}
              className='tw-h-full'
              columnResizeMode='expand'
              removableSort
              onSelectionChange={({
                value,
              }: {
                originalEvent: React.SyntheticEvent
                value: DocumentoComercialValidacionDtoProps[]
                type?: "multiple" | "all" | "checkbox" | "row" | undefined
              }) => {
                setSelection(value)
              }}
              value={items.sort(
                (
                  a: DocumentoComercialValidacionDtoProps,
                  b: DocumentoComercialValidacionDtoProps
                ) => {
                  const fechaA = a?.fechaEmision ?? ""
                  const fechaB = b?.fechaEmision ?? ""
                  return fechaA > fechaB ? -1 : 1
                }
              )}
              isDataSelectable={({ data }) => {
                if (
                  selection.length > 0 &&
                  selection[0].tipoDocumento !== data.tipoDocumento
                )
                  return false
                return true
              }}
              showGridlines
              selection={selection}
              selectionMode='checkbox'
              selectionColumnMode='multiple'
              dataKey='id'
              lazy
              scrollable
              size='small'
              loading={isLoading}
              emptyMessage={t("primeReact.table.emptyMessage")}
            />
          </div>
        </div>

        <ModalAutorizarDocumentos state={state} />
      </div>
    </div>
  )
}

export default Documentos
